/* eslint-disable no-await-in-loop -- IndexedDB cursors are inherently sequential */

import type { ContentType } from '@/interfaces';
import type { DBSchema, IDBPDatabase } from 'idb';
import { openDB } from 'idb';
import { isItemExpired } from './license';
import type { OfflineItem } from './types';

const DB_NAME = 'bnef-offline';
const DB_VERSION = 1;
const STORE = 'offline_items';

interface OfflineDB extends DBSchema {
    [STORE]: {
        key: string;
        value: OfflineItem;
    };
}

const getUserPrefix = (userId: number): string => `${userId}:`;

const getItemKey = (userId: number, contentType: ContentType, itemId: string): string =>
    `${getUserPrefix(userId)}${contentType}:${itemId}`;

export function getDb(): Promise<IDBPDatabase<OfflineDB>> {
    return openDB<OfflineDB>(DB_NAME, DB_VERSION, {
        upgrade(db) {
            if (!db.objectStoreNames.contains(STORE)) {
                db.createObjectStore(STORE);
            }
        },
    });
}

export async function saveItemToDb(userId: number, item: OfflineItem): Promise<void> {
    const db = await getDb();
    await db.put(STORE, item, getItemKey(userId, item.contentType, item.itemId));
}

export async function getItemFromDb(
    userId: number,
    contentType: ContentType,
    itemId: string
): Promise<OfflineItem | undefined> {
    const db = await getDb();
    return db.get(STORE, getItemKey(userId, contentType, itemId));
}

export async function getAllItemsForUser(userId: number): Promise<OfflineItem[]> {
    const db = await getDb();
    return db.getAll(STORE, IDBKeyRange.bound(getUserPrefix(userId), `${getUserPrefix(userId)}\uffff`));
}

export async function deleteItemFromDb(userId: number, contentType: ContentType, itemId: string): Promise<void> {
    const db = await getDb();
    await db.delete(STORE, getItemKey(userId, contentType, itemId));
}

export async function clearAllItemsFromDb(): Promise<void> {
    const db = await getDb();
    await db.clear(STORE);
}

export async function purgeItemsNotForUserFromDb(userId: number): Promise<number> {
    const db = await getDb();
    const prefix = getUserPrefix(userId);
    const tx = db.transaction(STORE, 'readwrite');
    let cursor = await tx.store.openCursor();
    let purged = 0;
    while (cursor) {
        if (!String(cursor.key).startsWith(prefix)) {
            await cursor.delete();
            purged++;
        }
        cursor = await cursor.continue();
    }
    await tx.done;
    return purged;
}

export async function deleteExpiredItemsFromDb(): Promise<number> {
    const db = await getDb();
    const tx = db.transaction(STORE, 'readwrite');
    let cursor = await tx.store.openCursor();
    let deleted = 0;
    while (cursor) {
        if (isItemExpired(cursor.value.downloadedAt)) {
            await cursor.delete();
            deleted++;
        }
        cursor = await cursor.continue();
    }
    await tx.done;
    return deleted;
}

export async function evictExpiredBlobsForUserFromDb(userId: number): Promise<number> {
    const db = await getDb();
    const prefix = getUserPrefix(userId);
    const tx = db.transaction(STORE, 'readwrite');
    let cursor = await tx.store.openCursor(IDBKeyRange.bound(prefix, `${prefix}\uffff`));
    let evicted = 0;
    while (cursor) {
        const item = cursor.value;
        if (item.pdf !== null && isItemExpired(item.downloadedAt)) {
            await cursor.update({ ...item, pdf: null });
            evicted++;
        }
        cursor = await cursor.continue();
    }
    await tx.done;
    return evicted;
}
